import { Box, Skeleton } from "@mui/material";

const LINE = "#E8EEE9";

// faux bar-pair heights (px), loosely echoing a status distribution
const BARS: [number, number][] = [
  [150, 140],
  [30, 26],
  [4, 4],
  [16, 10],
  [26, 40],
  [120, 130],
  [4, 4],
  [96, 60],
  [40, 52],
];

const StatCardSkeleton = () => (
  <Box
    sx={{
      flex: "1 1 240px",
      maxWidth: "360px",
      backgroundColor: "#fff",
      border: `1.5px solid ${LINE}`,
      borderRadius: "16px",
      padding: "16px 16px 14px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "14px",
      }}
    >
      <Skeleton variant="rounded" animation="wave" width={34} height={34} />
      <Skeleton
        variant="text"
        animation="wave"
        height={16}
        sx={{ flex: 1, maxWidth: 150 }}
      />
    </Box>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "18px" }}>
      <Skeleton variant="text" animation="wave" width={56} height={30} />
      <Skeleton variant="text" animation="wave" width={72} height={22} />
    </Box>
  </Box>
);

type DashboardTableSkeletonProps = {
  /** show the chart-card placeholder (dashboards without a chart pass false) */
  showChart?: boolean;
};

const DashboardTableSkeleton = ({
  showChart = true,
}: DashboardTableSkeletonProps) => {
  return (
    <Box sx={{ width: "100%", marginTop: "10px" }} aria-busy="true">
      {/* chart card */}
      {showChart && (
        <Box
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: "16px",
            padding: "20px",
          }}
        >
          {/* header: title + legend chips */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Skeleton
                variant="text"
                animation="wave"
                height={24}
                sx={{ width: "45%", maxWidth: 300 }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                height={14}
                sx={{ width: "60%", maxWidth: 380 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Skeleton
                variant="rounded"
                animation="wave"
                width={92}
                height={30}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                width={78}
                height={30}
              />
            </Box>
          </Box>

          {/* plot area: y-axis stub + bar pairs on a baseline */}
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingBottom: "28px",
                height: 260,
              }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  animation="wave"
                  width={22}
                  height={12}
                />
              ))}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-around",
                height: 260,
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              {BARS.map(([numH, areaH], i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-end", gap: "4px" }}
                  >
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      width={16}
                      height={numH}
                      sx={{ borderRadius: "4px 4px 0 0" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      width={16}
                      height={areaH}
                      sx={{ borderRadius: "4px 4px 0 0" }}
                    />
                  </Box>
                  <Skeleton
                    variant="text"
                    animation="wave"
                    height={10}
                    sx={{ width: "60%", minWidth: 30 }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* stat cards row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          justifyContent: "center",
          padding: "15px",
          marginTop: "10px",
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </Box>
    </Box>
  );
};

export default DashboardTableSkeleton;
